import { useQueryClient, type QueryClient } from '@tanstack/react-query'
import { classifyError } from './errors.ts'

/**
 * Telling a person what just happened, and letting them take it back.
 *
 * Every action that changes something ends in a notice that says exactly
 * what happened ("Removed “Prayer” from Sunday’s plan"), not "Saved". A
 * removal waits a few seconds before it is sent, so Undo is instant and
 * honest: nothing has happened yet. The notices are drawn by the platform
 * (web: a Base UI toast; `setNoticeSink`), so the words and the rules live
 * here, once.
 */

export interface Notice {
  /** What happened, in the past tense, naming the thing: “Removed Prayer”. */
  title: string
  /** The consequence or the next step: “10:17 now starts 10:13”. */
  description?: string
  tone?: 'success' | 'info' | 'error'
  /** One action: Undo, Retry, Open… */
  action?: { label: string; run: () => void }
  /** How long it stays, in ms; 0 stays until closed. */
  timeout?: number
  /** Replaces the notice with this id (a progress notice that finishes). */
  id?: string
}

type Sink = { show: (notice: Notice) => string; close: (id: string) => void }
let sink: Sink | null = null

/** The platform's way of drawing notices (web: the Toaster). */
export function setNoticeSink(next: Sink | null) {
  sink = next
}

/** Shows a notice; returns its id (empty when nothing draws notices, as in tests). */
export function notify(notice: Notice): string {
  return sink?.show(notice) ?? ''
}

export function closeNotice(id: string) {
  sink?.close(id)
}

/** A failure as a notice: what broke, what is safe, and Retry when it can work. */
export function notifyFailure(error: unknown, retry?: () => void, about?: string) {
  const failure = classifyError(error)
  return notify({
    tone: 'error',
    title: about ? `${about}: ${failure.title.toLowerCase()}` : failure.title,
    description: `${failure.detail} ${failure.safe}`,
    action: retry && failure.retryable ? { label: 'Retry', run: retry } : undefined,
    timeout: 8000,
  })
}

/**
 * Hides one record from every cached query (lists, pages, details' nested
 * lists): any array holding an object with this `id` loses it. Returns how
 * to put everything back exactly as it was.
 */
export function hideEverywhere(queryClient: QueryClient, id: string): () => void {
  const saved = queryClient.getQueriesData({})
  const without = (value: unknown): unknown => {
    if (Array.isArray(value)) {
      let changed = false
      const kept: unknown[] = []
      for (const item of value) {
        if (item && typeof item === 'object' && (item as { id?: unknown }).id === id) {
          changed = true
          continue
        }
        const next = without(item)
        if (next !== item) changed = true
        kept.push(next)
      }
      return changed ? kept : value
    }
    if (value && typeof value === 'object' && Object.getPrototypeOf(value) === Object.prototype) {
      let changed = false
      const next: Record<string, unknown> = {}
      for (const [key, inner] of Object.entries(value)) {
        const after = without(inner)
        if (after !== inner) changed = true
        next[key] = after
      }
      return changed ? next : value
    }
    return value
  }
  const changed = saved.filter(([key, data]) => {
    const next = without(data)
    if (next === data) return false
    queryClient.setQueryData(key, next)
    return true
  })
  return () => {
    for (const [key, data] of changed) queryClient.setQueryData(key, data)
  }
}

interface Pending {
  commit: () => void
}
/** Removals waiting out their Undo window. */
const pending = new Set<Pending>()

/**
 * Sends every removal still waiting out its Undo window, now. The platform
 * calls it when the app is about to go away (web: the page is hidden), so a
 * removal is never lost to a closed tab.
 */
export function flushPendingRemovals() {
  for (const p of [...pending]) p.commit()
}

export interface UndoableInput {
  /** The record's id: it disappears from every list at once. */
  id: string
  /** “Removed Prayer”. */
  title: string
  description?: string
  /** Sends the change (the mutation). Runs after the Undo window. */
  commit: () => Promise<unknown>
  /** Runs once it has been sent and accepted. */
  onDone?: () => void
  /** The Undo window, in ms. */
  delay?: number
}

/**
 * A removal that can be taken back: the record leaves every list now, a
 * notice offers Undo, and the request goes after `delay` (or as soon as the
 * page is hidden). Undo puts the lists back; nothing was sent. A failure puts
 * them back too, and says so, with Retry.
 */
export function useUndoableRemove() {
  const queryClient = useQueryClient()
  return function remove(input: UndoableInput) {
    const restore = hideEverywhere(queryClient, input.id)
    const delay = input.delay ?? 5000
    let settled = false
    const entry: Pending = {
      commit: () => {
        if (settled) return
        settled = true
        clearTimeout(timer)
        pending.delete(entry)
        input
          .commit()
          .then(() => input.onDone?.())
          .catch((error: unknown) => {
            restore()
            notifyFailure(error, () => remove(input), `Couldn’t remove it`)
          })
      },
    }
    const timer = setTimeout(entry.commit, delay)
    pending.add(entry)
    notify({
      title: input.title,
      description: input.description,
      tone: 'success',
      timeout: delay,
      action: {
        label: 'Undo',
        run: () => {
          if (settled) return
          settled = true
          clearTimeout(timer)
          pending.delete(entry)
          restore()
        },
      },
    })
  }
}
